/**
 * AppSidebar — single, polished left navigation (Linear/Notion style).
 * Pinned Favorites + Recently Visited at top, then modules as collapsible
 * groups (the active module auto-expands). Full-width content sits beside it.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronRight, Star, Clock } from 'lucide-react';
import { modules, findPage, slugify } from './modulesConfig';
import { useAuth } from '../../context/AuthContext';

const AppSidebar = ({ prefs, activeModuleId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, canRead } = useAuth();
  const { recents, isFavorite, toggleFavorite } = prefs;
  const isAdmin = user?.is_superuser || (user?.roles || []).includes('System Manager');
  const isAuditor = isAdmin || (user?.roles || []).includes('Auditor');
  const visibleModules = modules.filter(
    (m) => (!m.adminOnly || isAdmin) && (!m.auditOnly || isAuditor) && (!m.doctype || canRead(m.doctype))
  );
  // Paths the current user is allowed to see — used to filter Recents so we never
  // surface a route the user cannot access.
  const visiblePaths = new Set(
    visibleModules.flatMap((m) => (m.items || []).map((it) => it.path.split('?')[0]))
  );
  const visibleRecents = recents.filter((p) => visiblePaths.has(p.path.split('?')[0]));
  const [openId, setOpenId] = useState(activeModuleId);

  // Keep the active module expanded as the route changes.
  useEffect(() => {
    if (activeModuleId) setOpenId(activeModuleId);
  }, [activeModuleId]);

  const isActive = (path) => {
    const [base, qs] = path.split('?');
    return location.pathname === base && (qs ? location.search.includes(qs) : true);
  };

  const favPages = prefs.favorites
    .map((p) => findPage(p))
    .filter((p) => p && visiblePaths.has(p.path.split('?')[0]));

  const PageRow = ({ leaf }) => {
    const Icon = leaf.icon;
    const active = isActive(leaf.path);
    const fav = isFavorite(leaf.path);
    return (
      <div
        className={`group relative flex items-center gap-1 pr-1.5 rounded-md transition-colors duration-150 ${
          active ? 'bg-primary/10 ring-1 ring-primary/15' : 'hover:bg-gray-200/60'
        }`}
      >
        {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-primary" />}
        <button
          onClick={() => navigate(leaf.path)}
          data-testid={`nav-link-${slugify(leaf.label)}`}
          className={`flex-1 flex items-center gap-2 px-2.5 py-1.5 text-sm text-left rounded-md min-w-0 transition-colors duration-150 ${
            active ? 'text-primary font-medium' : 'text-gray-600'
          }`}
        >
          {Icon && <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-primary' : 'text-gray-400'}`} strokeWidth={1.5} />}
          <span className="truncate">{leaf.label}</span>
        </button>
        <button
          onClick={() => toggleFavorite(leaf.path)}
          data-testid={`favorite-toggle-${slugify(leaf.label)}`}
          title={fav ? 'Remove from favorites' : 'Add to favorites'}
          className={`p-1 rounded shrink-0 transition-opacity focus-visible:opacity-100 ${
            fav ? 'opacity-100 text-yellow-500' : 'opacity-0 group-hover:opacity-100 text-gray-300 hover:text-yellow-500'
          }`}
        >
          <Star className="w-3.5 h-3.5" fill={fav ? 'currentColor' : 'none'} strokeWidth={1.5} />
        </button>
      </div>
    );
  };

  const SectionLabel = ({ children }) => (
    <div className="px-2.5 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
      {children}
    </div>
  );

  return (
    <aside
      className="w-64 shrink-0 bg-gray-50 border-r border-gray-200 h-[calc(100vh-64px)] sticky top-[64px] overflow-y-auto scrollbar-thin"
      data-testid="app-sidebar"
    >
      <div className="p-2.5">
        {/* Favorites */}
        {favPages.length > 0 && (
          <div className="mb-1" data-testid="sidebar-favorites">
            <SectionLabel>Favorites</SectionLabel>
            <div className="space-y-0.5">
              {favPages.map((p) => (
                <PageRow key={`fav-${p.path}`} leaf={{ path: p.path, label: p.label, icon: p.icon }} />
              ))}
            </div>
          </div>
        )}

        {/* Recents */}
        {visibleRecents.length > 0 && (
          <div className="mb-1" data-testid="sidebar-recents">
            <SectionLabel>
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3" strokeWidth={1.5} /> Recent
              </span>
            </SectionLabel>
            <div className="space-y-0.5">
              {visibleRecents.slice(0, 4).map((p) => (
                <button
                  key={`rec-${p.path}`}
                  onClick={() => navigate(p.path)}
                  className="w-full flex items-center gap-2 px-2.5 py-1.5 text-sm text-left text-gray-500 hover:bg-gray-200/50 hover:text-gray-800 rounded-lg"
                >
                  <span className="truncate">{p.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Modules */}
        <SectionLabel>Modules</SectionLabel>
        <div className="space-y-0.5">
          {visibleModules.map((m) => {
            const Icon = m.icon;
            const open = openId === m.id;
            const isActiveModule = activeModuleId === m.id;
            return (
              <div key={m.id}>
                <button
                  onClick={() => setOpenId(open ? null : m.id)}
                  data-testid={`nav-module-${m.id}`}
                  className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-md transition-colors duration-150 ${
                    isActiveModule ? 'text-primary font-medium' : 'text-gray-600'
                  } hover:bg-gray-200/60`}
                >
                  <Icon className={`w-4 h-4 shrink-0 ${isActiveModule ? 'text-primary' : 'text-gray-500'}`} strokeWidth={1.5} />
                  <span className="flex-1 text-left text-sm truncate">{m.name}</span>
                  <ChevronRight
                    className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}
                    strokeWidth={1.5}
                  />
                </button>
                {open && (
                  <div className="ml-3.5 mt-0.5 mb-1 pl-2 border-l border-gray-200 space-y-0.5">
                    {m.groups.map((g, gi) => (
                      <div key={g.label || `g-${gi}`}>
                        {g.label && (
                          <div className="px-2.5 pt-1.5 pb-0.5 text-[10px] font-medium uppercase tracking-[0.1em] text-gray-400">
                            {g.label}
                          </div>
                        )}
                        {g.leaves.map((leaf) => (
                          <PageRow key={leaf.path} leaf={leaf} />
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </aside>
  );
};

export default AppSidebar;
